import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { color } from '../../app/theme';
import icSearch from '../../assets/images/svg/ic-search.svg';
import icClear from '../../assets/images/svg/ic-x-circle.svg';

const useStyles = theme => ({
  InputContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: 44,
    border: `1px solid ${color.$10}`,
    borderRadius: '22px',
    padding: '0 16px',
    backgroundColor: color.$7,
    '& > .MuiInput-root': {
      width: '100%',
    },
  },
  InputContainerFocus: {
    borderColor: color.$1,
  },
  Input: {
    ...theme.typography.subtitle1,
    padding: 0,
    width: '100%',
  },
  ClearButton: {
    cursor: 'pointer',
  },
  hidden: {
    visibility: 'hidden',
  },
  'root--error': {
    borderColor: color.$17,
  },
});

const useCombinedRefs = (...refs) => {
  const targetRef = useRef();

  useEffect(() => {
    refs.forEach(ref => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(targetRef.current);
      } else {
        // eslint-disable-next-line no-param-reassign
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
};

const TextFieldSearch = forwardRef((props, ref) => {
  const {
    classes,
    showClearButton,
    onChange,
    value,
    onEnter,
    onlyNum,
    onFocus,
    status,
    ...otherProps
  } = props;
  const innerRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, innerRef);
  const [isFocus, setFocus] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const clearButtonOnclick = () => {
    setInputValue('');
    innerRef.current.focus();
    if (onChange) {
      onChange('');
    }
  };

  const onChangeTextField = e => {
    let { value: textValue } = e.target;
    if (onlyNum) {
      textValue = textValue.replace(/\D+/g, '');
    }
    setInputValue(textValue);
    if (onChange) {
      onChange(textValue);
    }
  };

  const onKeyDownTextField = e => {
    if (e.keyCode === 27 /* ESC */) {
      clearButtonOnclick();
    }
    if (e.keyCode === 13) {
      onEnter(inputValue);
    }
  };

  const isDirty = innerRef.current ? innerRef.current.value !== '' : false;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setFocus(false);
      }}
    >
      <TextField
        inputRef={combinedRef}
        classes={{
          root: classnames(classes.InputContainer, {
            [classes.InputContainerFocus]: isFocus,
            [classes['root--error']]: status === 'error',
          }),
        }}
        type="text"
        onChange={onChangeTextField}
        InputProps={{
          classes: { input: classes.Input },
          disableUnderline: true,
          startAdornment: (
            <InputAdornment position="start">
              <img src={icSearch} alt="icon" />
            </InputAdornment>
          ),
          endAdornment: showClearButton && (
            <InputAdornment
              position="end"
              classes={{ root: classes.ClearButton }}
              onClick={clearButtonOnclick}
            >
              <img
                className={isFocus || isDirty ? undefined : classes.hidden}
                src={icClear}
                alt="icon"
              />
            </InputAdornment>
          ),
          onFocus: () => {
            setFocus(true);
            if (onFocus) onFocus();
          },
          onKeyDown: onKeyDownTextField,
        }}
        value={inputValue || ''}
        {...otherProps}
      />
    </ClickAwayListener>
  );
});

TextFieldSearch.defaultProps = {
  onEnter: () => {},
  showClearButton: true,
  status: 'default',
};

TextFieldSearch.propTypes = {
  onEnter: PropTypes.func,
  showClearButton: PropTypes.bool,
  status: PropTypes.oneOf(['default', 'warning', 'error']),
};

export default withStyles(useStyles)(TextFieldSearch);
